import type { FC } from "react";
import { memo, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import useLobSites from "app/components/compare/WebsiteQueryBar/useLobSites";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { CircularProgress } from "pro-features/components/Chart/src/components/annotations/Components/CircularProgress";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import LobEducationBanner from "./components/LobEducationBanner/LobEducationBanner";
import LobEmptyState from "./components/LobEmptyState/LobEmptyState";
import LobWarningBanner from "./components/LobWarningBanner/LobWarningBanner";
import LineOfBusinessTable from "./widgets/LineOfBusinessTable/LineOfBusinessTable";
import VisitsBreakdownCompareMode from "./widgets/VisitsBreakdown/compare/VisitsBreakdownCompareMode";
import VisitsBreakdownSingleMode from "./widgets/VisitsBreakdown/single/VisitsBreakdownSingleMode";
import VisitsOvertimeCompareMode from "./widgets/VisitsOvertime/compare/VisitsOvertimeCompareMode";
import VisitsOvertimeSingleMode from "./widgets/VisitsOvertime/single/VisitsOvertimeSingleMode";

const Container = styled(FlexColumn)`
    gap: 24px;
`;

const LOB_EDUCATION_BANNER_PREF_KEY = "lob_education_banner_closed";

const LineOfBusiness: FC = () => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");

    const [showBanner, setShowBanner] = useState(() => {
        const wasBannerClosed = PreferencesService.get(LOB_EDUCATION_BANNER_PREF_KEY);
        return !wasBannerClosed;
    });

    const { lobSites, isLobLoading, topAnalyzedLOBWebsites, compareModeWebsites } = useLobSites();

    const {
        params: { key },
    } = useCurrentStateAndParams();
    const isCompare = isCompareMode();
    const { includedSites, excludedSites } = useMemo(() => {
        const allSites = key?.split(",").filter(Boolean) || [];
        const lobNamesSet = new Set(lobSites.map((site) => site.name));

        const included: string[] = [];
        const excluded: string[] = [];

        allSites.forEach((site) => (lobNamesSet.has(site) ? included.push(site) : excluded.push(site)));

        return { includedSites: included, excludedSites: excluded };
    }, [key, lobSites]);

    const hasIncludedSites = includedSites.length > 0;

    const handleCloseBanner = async () => {
        await PreferencesService.add({
            [LOB_EDUCATION_BANNER_PREF_KEY]: true,
        });
        setShowBanner(false);
    };

    return (
        <Container>
            {isLobLoading ? (
                <CircularProgress />
            ) : !hasIncludedSites ? (
                <LobEmptyState
                    excludedSites={excludedSites}
                    topAnalyzedLOBWebsites={topAnalyzedLOBWebsites}
                    compareModeWebsites={compareModeWebsites}
                />
            ) : (
                <>
                    {showBanner ? <LobEducationBanner onClose={handleCloseBanner} /> : null}
                    {excludedSites.length > 0 ? <LobWarningBanner excludedSites={excludedSites} /> : null}
                    {isCompare ? (
                        <>
                            <VisitsBreakdownCompareMode subtitleFilters={subtitleFilters} />
                            <VisitsOvertimeCompareMode subtitleFilters={subtitleFilters} />
                        </>
                    ) : (
                        <>
                            <VisitsBreakdownSingleMode subtitleFilters={subtitleFilters} />
                            <VisitsOvertimeSingleMode subtitleFilters={subtitleFilters} />
                        </>
                    )}
                    <LineOfBusinessTable />
                </>
            )}
        </Container>
    );
};

export default memo(LineOfBusiness);
